import React, { useContext, useState } from 'react';
import { Box, Button, Collapse, Link, Typography } from '@mui/material';
import CookieRoundedIcon        from '@mui/icons-material/CookieRounded';
import BarChartRoundedIcon      from '@mui/icons-material/BarChartRounded';
import CheckCircleRoundedIcon   from '@mui/icons-material/CheckCircleRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import ShieldRoundedIcon        from '@mui/icons-material/ShieldRounded';

import useCookieConsent from '../../hooks/useCookieConsent';
import { ColorModeContext } from '../../App';

import './styles.scss';

// ── Cookie details data ───────────────────────────────────────────────────────

const COOKIE_ROWS = [
  {
    name:    '_ga',
    expires: '2 years',
    purpose: 'Distinguishes unique visitors so we can count how many people use the app.',
  },
  {
    name:    '_ga_XXXXXXX',
    expires: '2 years',
    purpose: 'Maintains session state for the current analytics session.',
  },
];

// ── Sub-components ────────────────────────────────────────────────────────────

const CookieRow = ({ name, expires, purpose, mode }) => (
  <Box className={`cc-cookie-row cc-cookie-row--${mode}`}>
    <Box className="cc-cookie-row__name">
      <Typography variant="caption" component="code" className="cc-cookie-code">
        {name}
      </Typography>
      <Typography variant="caption" className="cc-cookie-expires">
        {expires}
      </Typography>
    </Box>
    <Typography variant="caption" className="cc-cookie-purpose">
      {purpose}
    </Typography>
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────────

const CookieConsent = () => {
  const { mode }                                        = useContext(ColorModeContext);
  const { showBanner, acceptAnalytics, declineAnalytics } = useCookieConsent();
  const [detailsOpen, setDetailsOpen]                   = useState(false);

  if (!showBanner) return null;

  return (
    <Box
      role="dialog"
      aria-modal="false"
      aria-label="Cookie consent"
      className={`cc-root cc-root--${mode}`}
    >
      {/* ── Glow accent ── */}
      <Box className="cc-glow" aria-hidden="true" />

      <Box className="cc-inner">

        {/* ── Header row ── */}
        <Box className="cc-header">
          <Box className="cc-header__icon-wrap" aria-hidden="true">
            <CookieRoundedIcon className="cc-header__icon" />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={800} className="cc-title">
              We use cookies
            </Typography>
            <Typography variant="caption" className="cc-subtitle">
              To understand how you use NoteCORE so we can make it better.
            </Typography>
          </Box>
        </Box>

        {/* ── Essential vs analytics ── */}
        <Box className="cc-category-list">

          {/* Always-on: essential / localStorage */}
          <Box className={`cc-category cc-category--essential cc-category--${mode}`}>
            <Box className="cc-category__icon-wrap" aria-hidden="true">
              <ShieldRoundedIcon sx={{ fontSize: 16 }} />
            </Box>
            <Box className="cc-category__body">
              <Box className="cc-category__header">
                <Typography variant="caption" fontWeight={700} className="cc-category__name">
                  Essential (always on)
                </Typography>
                <Box className="cc-badge cc-badge--essential">Required</Box>
              </Box>
              <Typography variant="caption" className="cc-category__desc">
                Auth tokens, colour-mode preference, and editor state — stored in <strong>localStorage</strong>, not cookies. Never transmitted without your action.
              </Typography>
            </Box>
          </Box>

          {/* Opt-in: analytics */}
          <Box className={`cc-category cc-category--analytics cc-category--${mode}`}>
            <Box className="cc-category__icon-wrap" aria-hidden="true">
              <BarChartRoundedIcon sx={{ fontSize: 16 }} />
            </Box>
            <Box className="cc-category__body">
              <Box className="cc-category__header">
                <Typography variant="caption" fontWeight={700} className="cc-category__name">
                  Analytics — Google Analytics 4
                </Typography>
                <Box className="cc-badge cc-badge--optional">Optional</Box>
              </Box>
              <Typography variant="caption" className="cc-category__desc">
                Anonymous page views, session duration, and button-click events. Uses{' '}
                <strong>IP anonymisation</strong>. No advertising signals. No personal data is linked to analytics.
              </Typography>

              {/* Expandable cookie table */}
              <Button
                size="small"
                disableRipple
                endIcon={
                  <KeyboardArrowDownRoundedIcon
                    sx={{
                      fontSize: '16px !important',
                      transition: 'transform 0.2s',
                      transform: detailsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  />
                }
                onClick={() => setDetailsOpen((v) => !v)}
                className={`cc-details-toggle cc-details-toggle--${mode}`}
              >
                {detailsOpen ? 'Hide cookie details' : 'Show cookie details'}
              </Button>

              <Collapse in={detailsOpen} unmountOnExit>
                <Box className="cc-cookie-table">
                  <Box className={`cc-cookie-table__head cc-cookie-table__head--${mode}`}>
                    <Typography variant="caption" fontWeight={700}>Cookie name</Typography>
                    <Typography variant="caption" fontWeight={700}>Expires</Typography>
                    <Typography variant="caption" fontWeight={700}>Purpose</Typography>
                  </Box>
                  {COOKIE_ROWS.map((row) => (
                    <CookieRow key={row.name} {...row} mode={mode} />
                  ))}
                </Box>
              </Collapse>
            </Box>
          </Box>
        </Box>

        {/* ── Privacy notice ── */}
        <Typography variant="caption" className={`cc-privacy-note cc-privacy-note--${mode}`}>
          We never sell your data.{' '}
          <Link href="/privacy" className="cc-link">
            Read our Privacy Policy ↗
          </Link>
        </Typography>

        {/* ── Action buttons ── */}
        <Box className="cc-actions">
          <Button
            variant="text"
            size="small"
            onClick={declineAnalytics}
            className={`cc-btn-decline cc-btn-decline--${mode}`}
          >
            Decline analytics
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<CheckCircleRoundedIcon sx={{ fontSize: '16px !important' }} />}
            onClick={acceptAnalytics}
            className="cc-btn-accept"
          >
            Accept analytics
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default CookieConsent;
